// client/src/pages/Game/Battlefield/utils/combatAnimations.js
//
// Animations de combat — chocs, dégâts, destruction, sorts.
//
// Toutes les animations utilisent le Web Animations API (element.animate)
// pour composer librement les transforms sans conflit CSS, et des clones
// fixed-position pour les cartes déjà retirées du DOM par React.

import { animateFlight } from './cardFlight';

// ─── Couleurs par élément ──────────────────────────────────────────

export const ELEMENT_COLORS = {
  fire: '#ff6b35',
  water: '#4fc3f7',
  earth: '#8bc34a',
  light: '#ffe082',
  dark: '#b388ff',
  neutral: '#5acdff',
};

const elementColor = (card, getCardElement) =>
  ELEMENT_COLORS[(getCardElement ? getCardElement(card) : card?.element) || 'neutral']
  || ELEMENT_COLORS.neutral;

// ─── Helpers DOM ───────────────────────────────────────────────────

const centerOfRect = (r) => ({ x: r.left + r.width / 2, y: r.top + r.height / 2 });

const rectOf = (el) => el.getBoundingClientRect();

function makeCardNode(card, getCardElement) {
  const node = document.createElement('div');
  node.style.cssText = 'width:100%;height:100%;overflow:visible;';
  const img = document.createElement('img');
  // R34kk : art réel de la carte (comme CardModal) — le payload serveur
  // n'a pas de champ imageUrl ; /cards/<element>.svg n'est qu'une icône.
  const cardId = card && (card.cardId || card.id);
  img.src = card.imageUrl
    || (cardId ? `/api/static/images/cards/${cardId}.webp` : '')
    || `/api/static/images/cards/${getCardElement ? getCardElement(card) : 'fire'}.svg`;
  img.onerror = () => { img.src = '/api/static/images/card-backs/fire-back.svg'; };
  img.style.cssText = 'width:100%;height:100%;object-fit:cover;border-radius:5px;display:block;';
  node.appendChild(img);

  if (card.type === 'CHARACTER' && (card.attack !== undefined || card.defense !== undefined)) {
    const stats = document.createElement('div');
    stats.style.cssText = `
      position:absolute;left:0;right:0;bottom:0;
      display:flex;justify-content:space-between;
      background-color:rgba(0,0,0,0.7);padding:2px 4px;
      font-weight:bold;font-size:11px;color:white;border-radius:0 0 5px 5px;
    `;
    const atk = document.createElement('span');
    atk.style.cssText = 'background-color:rgba(255,60,40,0.85);padding:0 5px;border-radius:3px;';
    atk.textContent = card.attack ?? 0;
    const def = document.createElement('span');
    def.style.cssText = 'background-color:rgba(40,80,255,0.85);padding:0 5px;border-radius:3px;';
    def.textContent = card.defense ?? 0;
    stats.appendChild(atk);
    stats.appendChild(def);
    node.appendChild(stats);
  }
  return node;
}

/**
 * Clone visuel d'une carte à une position fixe du viewport.
 */
export function cloneCardAt(card, rect, getCardElement, extraClass = '') {
  const el = document.createElement('div');
  el.className = `combat-clone ${extraClass}`;
  el.style.cssText = `
    position:fixed;
    left:${rect.left}px;top:${rect.top}px;
    width:${rect.width}px;height:${rect.height}px;
    transform:translate(0,0);
    pointer-events:none;z-index:9998;
    filter:drop-shadow(0 4px 14px rgba(0,0,0,0.6));
    will-change:transform,filter,opacity;
  `;
  el.appendChild(makeCardNode(card, getCardElement));
  document.body.appendChild(el);
  return el;
}

/**
 * R34hh : showcase « effet déclenché » — la carte source s'affiche en GRAND
 * au centre de l'écran avec un balayage de brillance et un libellé, pour
 * montrer AU JOUEUR quelle carte vient de déclencher son effet. Durée
 * totale ~1200 ms ; les animations de dégâts sont décalées derrière.
 */
export function playCardShowcase({ card, label = 'Effet déclenché', getCardElement }) {
  const vh = window.innerHeight;
  const h = Math.min(400, vh * 0.46);
  const w = h * 0.7;
  const cx = window.innerWidth / 2;
  const cy = vh * 0.42;
  const color = elementColor(card, getCardElement);

  // Voile sombre derrière pour détacher la carte
  const veil = document.createElement('div');
  veil.style.cssText = [
    'position:fixed;left:0;top:0;width:100vw;height:100vh;z-index:10030;',
    'pointer-events:none;opacity:0;',
    'background:radial-gradient(ellipse at center, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 55%, rgba(0,0,0,0) 80%);',
  ].join('');
  document.body.appendChild(veil);

  const clone = cloneCardAt(card, { left: cx - w / 2, top: cy - h / 2, width: w, height: h }, getCardElement, 'card-showcase');
  clone.style.zIndex = '10031';
  clone.style.opacity = '0';
  clone.style.filter = 'drop-shadow(0 10px 30px rgba(0,0,0,0.7)) drop-shadow(0 0 26px ' + color + ')';

  // Balayage de brillance (barre diagonale blanche)
  const shine = document.createElement('div');
  shine.style.cssText = [
    'position:absolute;left:-70%;top:-20%;width:45%;height:150%;',
    'transform:rotate(18deg) translateX(0);pointer-events:none;',
    'background:linear-gradient(90deg, transparent, rgba(255,255,255,0.75), transparent);',
    'filter:blur(3px);',
  ].join('');
  clone.appendChild(shine);

  // Libellé sous la carte
  const tag = document.createElement('div');
  tag.textContent = (card && card.name ? card.name + ' — ' : '') + label;
  tag.style.cssText = [
    'position:fixed;left:' + cx + 'px;top:' + (cy + h / 2 + 18) + 'px;',
    'transform:translate(-50%,0);color:#fff;font-weight:900;',
    'font-size:clamp(14px,2.2vh,20px);letter-spacing:0.06em;text-transform:uppercase;',
    'text-shadow:0 0 10px ' + color + ',0 2px 4px rgba(0,0,0,0.9);',
    'pointer-events:none;z-index:10032;opacity:0;',
  ].join('');
  document.body.appendChild(tag);

  veil.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 160, fill: 'forwards' });
  clone.animate([
    { transform: 'translate(0,34px) scale(0.55) rotate(-6deg)', opacity: 0 },
    { transform: 'translate(0,0) scale(1.06) rotate(1deg)', opacity: 1, offset: 0.55 },
    { transform: 'translate(0,0) scale(1) rotate(0deg)', opacity: 1 },
  ], { duration: 340, easing: 'cubic-bezier(0.2,0.9,0.3,1.2)', fill: 'forwards' });
  tag.animate([
    { opacity: 0, transform: 'translate(-50%,10px)' },
    { opacity: 1, transform: 'translate(-50%,0)' },
  ], { duration: 240, delay: 180, fill: 'forwards' });
  shine.animate([
    { transform: 'rotate(18deg) translateX(0)' },
    { transform: 'rotate(18deg) translateX(' + (w * 3.4) + 'px)' },
  ], { duration: 620, delay: 330, easing: 'cubic-bezier(0.3,0.6,0.4,1)', fill: 'forwards' });
  shockwave(cx, cy, { color, size: h * 1.5, duration: 520, delay: 120, width: 2 });

  const cleanup = () => { veil.remove(); clone.remove(); tag.remove(); };
  // Sortie
  clone.animate([
    { transform: 'translate(0,0) scale(1)', opacity: 1 },
    { transform: 'translate(0,-18px) scale(0.85)', opacity: 0 },
  ], { duration: 260, delay: 980, easing: 'ease-in', fill: 'forwards' })
    .finished.then(cleanup).catch(cleanup);
  tag.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 200, delay: 1000, fill: 'forwards' });
  veil.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 260, delay: 1000, fill: 'forwards' });
  return 1240; // durée indicative (ms)
}

/**
 * Nombre de dégâts flottant (rouge) ou soin (vert) qui monte et s'efface.
 */
export function floatText(text, x, y, { color = '#ff5252', size = 20, delay = 0 } = {}) {
  const el = document.createElement('div');
  el.textContent = text;
  el.style.cssText = `
    position:fixed;left:${x}px;top:${y}px;
    transform:translate(-50%,-50%);
    color:${color};font-size:${size}px;font-weight:900;
    font-family:inherit;
    text-shadow:0 0 6px rgba(0,0,0,0.9),0 2px 4px rgba(0,0,0,0.8);
    pointer-events:none;z-index:10000;
    will-change:transform,opacity;
  `;
  document.body.appendChild(el);
  el.animate([
    { transform: 'translate(-50%,-50%) scale(0.6)', opacity: 0 },
    { transform: 'translate(-50%,-70%) scale(1.25)', opacity: 1, offset: 0.25 },
    { transform: 'translate(-50%,-130%) scale(1)', opacity: 1, offset: 0.7 },
    { transform: 'translate(-50%,-180%) scale(0.9)', opacity: 0 },
  ], { duration: 950, delay, easing: 'ease-out', fill: 'forwards' })
    .finished.then(() => el.remove()).catch(() => el.remove());
}

/**
 * Anneau de choc expansif (impact, sorts, PV).
 */
export function shockwave(x, y, { color = '#ffffff', size = 140, duration = 450, delay = 0, width = 3 } = {}) {
  const el = document.createElement('div');
  el.style.cssText = `
    position:fixed;left:${x}px;top:${y}px;
    width:${size}px;height:${size}px;
    border-radius:50%;
    border:${width}px solid ${color};
    transform:translate(-50%,-50%) scale(0.1);
    pointer-events:none;z-index:9999;
    box-shadow:0 0 18px ${color};
    will-change:transform,opacity;
  `;
  document.body.appendChild(el);
  el.animate([
    { transform: 'translate(-50%,-50%) scale(0.1)', opacity: 1 },
    { transform: 'translate(-50%,-50%) scale(1)', opacity: 0 },
  ], { duration, delay, easing: 'cubic-bezier(0.2,0.6,0.3,1)', fill: 'forwards' })
    .finished.then(() => el.remove()).catch(() => el.remove());
}

/**
 * Tremblement de tout le champ de bataille (impact de combat).
 */
export function shakeBattlefield(intensity = 1) {
  const frame = document.querySelector('.battlefield-frame');
  if (!frame) return;
  const d = 3 * intensity;
  frame.animate([
    { transform: 'translate(0,0)' },
    { transform: `translate(${d}px, ${-d / 2}px)` },
    { transform: `translate(${-d}px, ${d / 3}px)` },
    { transform: `translate(${d * 0.6}px, ${-d / 4}px)` },
    { transform: `translate(${-d * 0.4}px, ${d / 5}px)` },
    { transform: 'translate(0,0)' },
  ], { duration: 300, easing: 'ease-out' });
}

// ─── 1. Choc de combat ─────────────────────────────────────────────

/**
 * Chorégraphie du choc : les clones d'attaquants foncent sur les défenseurs,
 * impact (onde de choc + tremblement du champ), puis recul.
 *
 * participants: [{ card, rect, role: 'attacker'|'defender', side: 'player'|'opponent' }]
 */
export function playCombatClash({ participants, getCardElement, onResolved }) {
  const atk = participants.filter(p => p.role === 'attacker');
  const def = participants.filter(p => p.role !== 'attacker');

  const atkCenter = atk.length
    ? centerOfRect(atk[0].rect)
    : { x: window.innerWidth / 2, y: window.innerHeight / 2 };
  const defCenter = def.length
    ? centerOfRect(def[0].rect)
    : { x: window.innerWidth / 2, y: Math.max(40, atkCenter.y - 160) };

  // Point d'impact : milieu entre les deux groupes
  const mid = { x: (atkCenter.x + defCenter.x) / 2, y: (atkCenter.y + defCenter.y) / 2 };

  const clones = participants.map(p => ({
    p,
    el: cloneCardAt(p.card, p.rect, getCardElement),
  }));

  // Chaque attaquant fonce vers l'impact ; les défenseurs encaissent (recul)
  const LUNGE_MS = 210;
  const KNOCK_MS = 340;

  clones.forEach(({ p, el }) => {
    const c = centerOfRect(p.rect);
    const dx = mid.x - c.x;
    const dy = mid.y - c.y;
    const dist = Math.hypot(dx, dy) || 1;

    if (p.role === 'attacker') {
      // Fonce vers l'impact (85% du chemin), frappe, revient
      const travel = 0.85;
      el.animate([
        { transform: 'translate(0,0) rotate(0deg) scale(1)' },
        {
          transform: 'translate(' + (dx * -0.09) + 'px,' + (dy * -0.09) + 'px) rotate(' + (dx > 0 ? -4 : 4) + 'deg) scale(1.07)',
          offset: 0.18,
          easing: 'ease-out',
        },
        {
          transform: `translate(${dx * travel}px, ${dy * travel}px) rotate(${dx > 0 ? 6 : -6}deg) scale(1.08)`,
          offset: 0.62,
          easing: 'ease-in',
        },
        {
          transform: `translate(${dx * travel * 0.72}px, ${dy * travel * 0.72}px) rotate(0deg) scale(1.02)`,
          offset: 0.78,
        },
        { transform: 'translate(0,0) rotate(0deg) scale(1)' },
      ], { duration: LUNGE_MS + KNOCK_MS, easing: 'ease-out' });
    } else {
      // Encaisse : léger sursaut vers l'impact au moment du coup, puis recul
      el.animate([
        { transform: 'translate(0,0)' },
        { transform: 'translate(0,0)', offset: 0.6 },
        {
          transform: `translate(${-dx * 0.08}px, ${-dy * 0.08}px)`,
          offset: 0.72,
          easing: 'ease-out',
        },
        { transform: 'translate(0,0)' },
      ], { duration: LUNGE_MS + KNOCK_MS, easing: 'linear' });
    }
  });

  // Impact au moment où l'attaquant touche
  setTimeout(() => {
    shockwave(mid.x, mid.y, { color: '#ffd166', size: 170, duration: 480 });
    shockwave(mid.x, mid.y, { color: '#ffffff', size: 90, duration: 300, width: 2 });
    shakeBattlefield(1);

    // Tremblement des clones après l'impact
    clones.forEach(({ el }) => {
      el.animate([
        { transform: 'translate(0,0)' },
        { transform: 'translate(-8px, 2px)' },
        { transform: 'translate(7px, -2px)' },
        { transform: 'translate(-5px, 1px)' },
        { transform: 'translate(3px, 0)' },
        { transform: 'translate(0,0)' },
      ], { duration: 320, easing: 'ease-out' });
    });
  }, LUNGE_MS);

  // Fin : nettoyer les clones, laisser la suite (retours / morts)
  setTimeout(() => {
    clones.forEach(({ el }) => {
      el.animate(
        [{ opacity: 1 }, { opacity: 0 }],
        { duration: 120, fill: 'forwards' }
      ).finished.then(() => el.remove()).catch(() => el.remove());
    });
    if (onResolved) onResolved();
  }, LUNGE_MS + KNOCK_MS + 140);
}

/**
 * Attaque directe : le clone fonce sur le cercle de PV du défenseur.
 */
export function playDirectAttack({ card, rect, lifeEl, targetEl, getCardElement, amount, onLanded }) {
  if (!lifeEl) { if (onLanded) onLanded(); return; }
  const owns = hideRealCard(targetEl);
  let landed = false;
  const finishAll = () => {
    if (landed) return;
    landed = true;
    clone.remove();
    if (owns) restoreRealCard(targetEl);
    if (onLanded) onLanded();
  };

  const lifeC = centerOfRect(rectOf(lifeEl));
  const c = centerOfRect(rect);
  const dx = lifeC.x - c.x;
  const dy = lifeC.y - c.y;
  const dist = Math.hypot(dx, dy) || 1;
  const ux = dx / dist;
  const uy = dy / dist;

  const clone = cloneCardAt(card, rect, getCardElement);
  clone.style.left = c.x + 'px';
  clone.style.top = c.y + 'px';
  clone.style.transform = 'translate(-50%,-50%)';

  const guard = setTimeout(finishAll, 6000);

  // 1) ANTICIPATION : leger retrait en arriere + charge lumineuse
  clone.animate([
    { transform: 'translate(-50%,-50%) translate(0,0) scale(1)', filter: 'brightness(1)' },
    {
      transform: 'translate(-50%,-50%) translate(' + (-ux * 16) + 'px,' + (-uy * 16) + 'px) scale(1.09)',
      filter: 'brightness(1.45) drop-shadow(0 0 12px #ffd166)',
      easing: 'ease-out',
    },
  ], { duration: 170, fill: 'forwards' })
    .finished
    // 2) FRAPPE : dash rapide vers le cercle de PV
    .then(() => clone.animate([
      {
        transform: 'translate(-50%,-50%) translate(' + (-ux * 16) + 'px,' + (-uy * 16) + 'px) scale(1.09)',
        filter: 'brightness(1.45) drop-shadow(0 0 12px #ffd166)',
      },
      {
        transform: 'translate(-50%,-50%) translate(' + dx + 'px,' + dy + 'px) scale(0.86) rotate(' + (dx > 0 ? 12 : -12) + 'deg)',
        filter: 'brightness(1.8) drop-shadow(0 0 16px #ff5252)',
        easing: 'cubic-bezier(0.55, 0, 0.9, 0.4)',
      },
    ], { duration: Math.min(330, Math.max(190, dist * 0.45)), fill: 'forwards' }).finished)
    .then(() => {
      shockwave(lifeC.x, lifeC.y, { color: '#ff5252', size: 150, duration: 500 });
      shockwave(lifeC.x, lifeC.y, { color: '#ffffff', size: 80, duration: 320, width: 2 });
      shakeBattlefield(0.8);
      playLifeHit(lifeEl, amount);

      // 3) RETOUR : arc de cercle vers sa vraie carte en zone monstres
      const cardC = targetEl ? centerOfRect(rectOf(targetEl)) : c;
      animateFlight(clone, {
        from: lifeC,
        to: cardC,
        duration: 560,
        rotation: { from: dx > 0 ? 12 : -12, to: 0 },
        spin: -14,
        scale: { from: 0.95, to: 1 },
        onLand: () => { clearTimeout(guard); finishAll(); },
      });
    })
    .catch(() => { clearTimeout(guard); finishAll(); });
}

export function playCardDeath({ card, rect, getCardElement, graveyardEl, delay = 0 }) {
  const c = centerOfRect(rect);
  const color = elementColor(card, getCardElement);

  setTimeout(() => {
    // Flash d'annonce
    shockwave(c.x, c.y, { color, size: rect.width * 1.6, duration: 380, width: 2 });

    const clone = cloneCardAt(card, rect, getCardElement);

    // Dissolution du corps
    clone.animate([
      { filter: 'brightness(2.2) saturate(1.4)', transform: 'translate(0,0) scale(1.04) rotate(0deg)', opacity: 1 },
      { filter: 'grayscale(0.7) brightness(0.7)', transform: 'translate(0,0) scale(1.1) rotate(3deg)', opacity: 0.85, offset: 0.4 },
      { filter: 'grayscale(1) brightness(0.25) blur(3px)', transform: 'translate(0,6px) scale(0.55) rotate(-10deg)', opacity: 0 },
    ], { duration: 680, delay: 60, easing: 'ease-in', fill: 'forwards' })
      .finished.then(() => clone.remove()).catch(() => clone.remove());

    // Éclats (fragments de la carte) — 6 morceaux en grille 3x2
    for (let i = 0; i < 6; i++) {
      const col = i % 3;
      const row = Math.floor(i / 3);
      const shard = document.createElement('div');
      shard.style.cssText = `
        position:fixed;
        left:${rect.left + (col * rect.width) / 3}px;
        top:${rect.top + (row * rect.height) / 2}px;
        width:${rect.width / 3}px;height:${rect.height / 2}px;
        background-image:url(${card.imageUrl
          || `/api/static/images/cards/${(card && (card.cardId || card.id)) || (getCardElement ? getCardElement(card) : 'fire')}.webp`});
        background-size:${rect.width}px ${rect.height}px;
        background-position:${-col * (rect.width / 3)}px ${-row * (rect.height / 2)}px;
        border-radius:4px;
        pointer-events:none;z-index:9997;
        filter:brightness(1.4) saturate(1.2);
        opacity:0;
        will-change:transform,opacity;
      `;
      document.body.appendChild(shard);
      const ang = (Math.PI * 2 * i) / 6 + (Math.random() - 0.5) * 0.8;
      const force = 50 + Math.random() * 70;
      shard.animate([
        {
          opacity: 1,
          transform: 'translate(0,0) rotate(0deg)',
        },
        {
          opacity: 0,
          transform: `translate(${Math.cos(ang) * force}px, ${Math.sin(ang) * force + 40}px)
                      rotate(${(Math.random() - 0.5) * 180}deg) scale(0.5)`,
        },
      ], { duration: 620, delay: 120, easing: 'cubic-bezier(0.15,0.6,0.4,1)', fill: 'forwards' })
        .finished.then(() => shard.remove()).catch(() => shard.remove());
    }

    // Fantôme vers le cimetière
    if (graveyardEl) {
      setTimeout(() => {
        const g = centerOfRect(rectOf(graveyardEl));
        const ghost = document.createElement('div');
        ghost.style.cssText = `
          position:fixed;left:${c.x}px;top:${c.y}px;
          width:${rect.width * 0.55}px;height:${rect.height * 0.55}px;
          transform:translate(-50%,-50%);
          pointer-events:none;z-index:9996;
          opacity:0.9;border-radius:5px;
          will-change:left,top,transform;
        `;
        const inner = makeCardNode({ ...card, type: card.type, attack: undefined, defense: undefined }, getCardElement);
        inner.style.filter = 'grayscale(0.8) brightness(0.7)';
        ghost.appendChild(inner);
        document.body.appendChild(ghost);
        animateFlight(ghost, {
          from: c,
          to: g,
          duration: 620,
          rotation: { from: -6, to: 0 },
          spin: 18,
          scale: { from: 1, to: 0.6 },
          onLand: () => {
            ghost.animate([{ opacity: 0.9 }, { opacity: 0 }], { duration: 180, fill: 'forwards' })
              .finished.then(() => ghost.remove()).catch(() => ghost.remove());
            flashGraveyard(graveyardEl);
          },
        });
      }, 260);
    }
  }, delay);
}

function flashGraveyard(el) {
  if (!el) return;
  el.animate([
    { filter: 'brightness(1)' },
    { filter: 'brightness(1.9)' },
    { filter: 'brightness(1)' },
  ], { duration: 350 });
}

// ─── 3. Lancement de sort ──────────────────────────────────────────

/**
 * Sort révélé : la carte monte, éclat élémentaire, dissolution.
 * caster: 'player' | 'opponent'
 */
export function playSpellCast({ card, caster, getCardElement, battlefieldEl }) {
  const bf = battlefieldEl || document.querySelector('.battlefield-frame');
  if (!bf) return;
  const bfR = rectOf(bf);
  const color = elementColor(card, getCardElement);

  // Position : tiers du lanceur
  const c = {
    x: bfR.left + bfR.width / 2,
    y: caster === 'player' ? bfR.top + bfR.height * 0.68 : bfR.top + bfR.height * 0.32,
  };
  const w = Math.min(130, bfR.width * 0.16);
  const h = w * 1.5;
  const rect = { left: c.x - w / 2, top: c.y - h / 2, width: w, height: h };

  const clone = cloneCardAt(card, rect, getCardElement);
  clone.style.transform = 'translate(0,26px) scale(0.7)';
  clone.style.opacity = '0';

  // Apparition + montée
  clone.animate([
    { transform: 'translate(0,26px) scale(0.7)', opacity: 0, filter: 'brightness(1)' },
    { transform: 'translate(0,0) scale(1)', opacity: 1, filter: `brightness(1.7) drop-shadow(0 0 16px ${color})` },
  ], { duration: 260, easing: 'ease-out', fill: 'forwards' })
    .finished.then(() => {
      // Éclat élémentaire
      shockwave(c.x, c.y, { color, size: w * 2.6, duration: 520 });
      shockwave(c.x, c.y, { color: '#ffffff', size: w * 1.4, duration: 340, width: 2 });
      shakeBattlefield(0.5);

      // Dissolution
      clone.animate([
        { transform: 'translate(0,0) scale(1) rotate(0deg)', opacity: 1, filter: `brightness(1.7) drop-shadow(0 0 16px ${color})` },
        { transform: `translate(0,-14px) scale(1.18) rotate(${caster === 'player' ? -5 : 5}deg)`, opacity: 1, offset: 0.5, filter: `brightness(2.1) drop-shadow(0 0 22px ${color})` },
        { transform: `translate(0,-26px) scale(0.6) rotate(${caster === 'player' ? -14 : 14}deg)`, opacity: 0, filter: `grayscale(1) blur(4px) brightness(0.4)` },
      ], { duration: 520, easing: 'ease-in', fill: 'forwards' })
        .finished.then(() => clone.remove()).catch(() => clone.remove());

      // Fantôme vers le cimetière du lanceur
      const gy = document.querySelector(`.${caster}-deck .deck-card`);
      if (gy) {
        const g = centerOfRect(rectOf(gy));
        const ghost = document.createElement('div');
        ghost.style.cssText = `
          position:fixed;left:${c.x}px;top:${c.y}px;
          width:${w * 0.55}px;height:${h * 0.55}px;
          transform:translate(-50%,-50%);
          pointer-events:none;z-index:9996;opacity:0.85;
          will-change:left,top,transform;
        `;
        ghost.appendChild(makeCardNode({ ...card, attack: undefined, defense: undefined }, getCardElement));
        document.body.appendChild(ghost);
        animateFlight(ghost, {
          from: { x: c.x, y: c.y - 20 },
          to: g,
          duration: 600,
          spin: 20,
          scale: { from: 1, to: 0.6 },
          onLand: () => {
            ghost.animate([{ opacity: 0.85 }, { opacity: 0 }], { duration: 160, fill: 'forwards' })
              .finished.then(() => ghost.remove()).catch(() => ghost.remove());
            flashGraveyard(gy);
          },
        });
      }
    }).catch(() => clone.remove());
}

/**
 * Pose de piège : reflet violet discret (le piège reste secret).
 */
export function playTrapSet({ caster }) {
  const zone = document.querySelector(`.${caster === 'player' ? 'player' : 'opponent'}-monsters`)
    || document.querySelector('.battlefield-frame');
  if (!zone) return;
  const c = centerOfRect(rectOf(zone));
  shockwave(c.x, c.y, { color: '#b388ff', size: 110, duration: 420, width: 2 });
}

// ─── 3b. Dégâts par EFFET (invocation / sort) ──────────────────────

/**
 * Projectile élémentaire : orbe lumineux qui file de la source vers la cible,
 * puis onde de choc à l'impact.
 */
export function playEffectBolt({ from, toEl, color = ELEMENT_COLORS.neutral, onImpact }) {
  if (!toEl) { if (onImpact) onImpact(); return null; }
  const to = centerOfRect(rectOf(toEl));
  const el = document.createElement('div');
  el.style.cssText = `
    position:fixed;left:${from.x}px;top:${from.y}px;
    width:26px;height:26px;border-radius:50%;
    background:radial-gradient(circle, #ffffff 0%, ${color} 45%, transparent 72%);
    box-shadow:0 0 18px ${color}, 0 0 34px ${color};
    transform:translate(-50%,-50%);
    pointer-events:none;z-index:10000;
    will-change:transform,opacity;
  `;
  document.body.appendChild(el);
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const dist = Math.hypot(dx, dy) || 1;
  el.animate([
    { transform: 'translate(-50%,-50%) translate(0,0) scale(1.15)', opacity: 1 },
    { transform: `translate(-50%,-50%) translate(${dx}px,${dy}px) scale(0.65)`, opacity: 0.95 },
  ], { duration: Math.min(480, Math.max(220, dist * 0.5)), easing: 'cubic-bezier(0.35,0,0.85,0.55)', fill: 'forwards' })
    .finished
    .then(() => {
      el.remove();
      shockwave(to.x, to.y, { color, size: 130, duration: 420 });
      shockwave(to.x, to.y, { color: '#ffffff', size: 70, duration: 260, width: 2 });
      if (onImpact) onImpact();
    })
    .catch(() => { el.remove(); if (onImpact) onImpact(); });
  return el;
}

/**
 * Coup porté sur une carte par un effet : onde de choc, recul marqué,
 * flash et nombre de dégâts. Plus visible qu'un simple flash.
 */
export function playEffectHit(el, amount, color = '#ff5252') {
  if (!el) return;
  const r = rectOf(el);
  const c = centerOfRect(r);

  el.animate([
    { filter: 'brightness(1)' },
    { filter: 'brightness(2) saturate(2) hue-rotate(-25deg)' },
    { filter: 'brightness(1)' },
  ], { duration: 420 });

  el.animate([
    { transform: 'translate(0,0) rotate(0deg)' },
    { transform: 'translate(-10px,2px) rotate(-3deg)' },
    { transform: 'translate(8px,-2px) rotate(2deg)' },
    { transform: 'translate(-5px,1px) rotate(-1deg)' },
    { transform: 'translate(0,0) rotate(0deg)' },
  ], { duration: 420, easing: 'ease-out' });

  if (amount > 0) {
    floatText(`-${amount}`, c.x, r.top - 4, { color: '#ff5252', size: Math.min(32, 17 + amount * 2) });
  }
}

/**
 * R34ee : changement de stats (buff/debuff ATK/DEF) — halo vert ou rouge
 * + texte flottant « +2 ATK » / « -1 PV » pour être compris du joueur.
 */
export function playStatFlash(el, { atk = 0, def = 0 } = {}) {
  if (!el) return;
  const r = rectOf(el);
  const c = centerOfRect(r);
  const gain = atk > 0 || def > 0;
  const color = gain ? '#4cd964' : '#ff5252';

  el.animate([
    { boxShadow: '0 0 0px ' + color },
    { boxShadow: '0 0 22px ' + color },
    { boxShadow: '0 0 0px ' + color },
  ], { duration: 900, easing: 'ease-out' });

  if (atk !== 0) {
    floatText((atk > 0 ? '+' : '') + atk + ' ATK', c.x, r.top - 2, {
      color,
      size: 20,
      delay: 0,
    });
  }
  if (def !== 0) {
    floatText((def > 0 ? '+' : '') + def + ' PV', c.x, r.top - 2, {
      color,
      size: 20,
      delay: (atk !== 0) ? 260 : 0,
    });
  }
}

/**
 * R34ee : gain de PV sur le cercle de vie — anneau vert + « +X ».
 */
export function playLifeGain(el, amount) {
  if (!el || !amount) return;
  const r = el.getBoundingClientRect();
  const c = { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  const ring = document.createElement('div');
  ring.style.cssText = `
    position:fixed;left:${c.x}px;top:${c.y}px;
    width:${Math.max(r.width, 60)}px;height:${Math.max(r.height, 60)}px;
    border-radius:50%;transform:translate(-50%,-50%);
    border:3px solid #4cd964;box-shadow:0 0 24px #4cd964, inset 0 0 18px rgba(76,217,100,0.55);
    pointer-events:none;z-index:10000;
  `;
  document.body.appendChild(ring);
  ring.animate([
    { transform: 'translate(-50%,-50%) scale(0.82)', opacity: 1 },
    { transform: 'translate(-50%,-50%) scale(1.45)', opacity: 0 },
  ], { duration: 700, easing: 'ease-out' }).finished
    .then(() => ring.remove()).catch(() => ring.remove());
  floatText('+' + amount, c.x, r.top - 6, { color: '#4cd964', size: 24 });
}

// ─── 4. Dégâts sur cartes réelles (encore dans le DOM) ─────────────

/**
 * Flash rouge + tremblement + nombre flottant sur une carte blessée.
 */
export function playDamageFlash(el, amount) {
  if (!el) return;
  const r = rectOf(el);
  const c = centerOfRect(r);

  el.animate([
    { filter: 'brightness(1)' },
    { filter: 'brightness(1.8) saturate(2) hue-rotate(-25deg)' },
    { filter: 'brightness(1)' },
  ], { duration: 380 });

  el.animate([
    { transform: 'translate(0,0)' },
    { transform: 'translate(-6px,1px)' },
    { transform: 'translate(5px,-1px)' },
    { transform: 'translate(-3px,0)' },
    { transform: 'translate(0,0)' },
  ], { duration: 300, easing: 'ease-out' });

  if (amount > 0) {
    floatText(`-${amount}`, c.x, r.top - 4, { color: '#ff5252', size: Math.min(30, 16 + amount * 2) });
  }
}

/**
 * Soin : flash vert + nombre flottant.
 */
export function playHealFlash(el, amount) {
  if (!el || !amount) return;
  const r = rectOf(el);
  const c = centerOfRect(r);
  el.animate([
    { filter: 'brightness(1)' },
    { filter: 'brightness(1.35) hue-rotate(70deg)' },
    { filter: 'brightness(1)' },
  ], { duration: 420 });
  floatText(`+${amount}`, c.x, r.top - 4, { color: '#69f0ae', size: 20 });
}

// ─── 5. Impact sur les points de vie ───────────────────────────────

/**
 * Coup sur un cercle de PV : anneau rouge + flash + nombre.
 */
export function playLifeHit(lifeEl, amount) {
  if (!lifeEl) return;
  const r = rectOf(lifeEl);
  const c = centerOfRect(r);

  lifeEl.animate([
    { filter: 'brightness(1)' },
    { filter: 'brightness(2) saturate(2) hue-rotate(-30deg) drop-shadow(0 0 14px #ff1744)' },
    { filter: 'brightness(1)' },
  ], { duration: 480 });

  lifeEl.animate([
    { transform: 'translate(0,0) scale(1)' },
    { transform: 'translate(2px,-1px) scale(1.14)' },
    { transform: 'translate(-2px,1px) scale(1.06)' },
    { transform: 'translate(0,0) scale(1)' },
  ], { duration: 360, easing: 'ease-out' });

  if (amount > 0) {
    floatText(`-${amount}`, c.x, r.top - 6, { color: '#ff1744', size: 30 });
  }
}

// ─── 6. Retour d'un survivant vers sa vraie carte ──────────────────

/**
 * Le clone d'un survivant vole de sa position de combat vers sa vraie
 * carte (masquée pendant le vol, flashée à l'atterrissage).
 */
export function hideRealCard(el) {
  if (!el || el.dataset.flying === '1') return false;
  el.dataset.flying = '1';
  el.dataset.flyingSince = String(Date.now());
  el.dataset.prevOpacity = el.style.opacity;
  el.style.transition = 'none';
  el.style.opacity = '0';
  return true;
}

export function restoreRealCard(el, { flash = true } = {}) {
  if (!el || el.dataset.flying !== '1') return;
  el.style.opacity = el.dataset.prevOpacity || '1';
  el.style.transition = '';
  delete el.dataset.flying;
  delete el.dataset.prevOpacity;
  if (flash && el.animate) {
    el.animate([
      { filter: 'brightness(1)' },
      { filter: 'brightness(1.6)' },
      { filter: 'brightness(1)' },
    ], { duration: 260 });
  }
}

export function flyCloneToCard({ card, rect, targetEl, getCardElement, owned = false }) {
  if (!targetEl) return;
  const to = centerOfRect(rectOf(targetEl));
  const c = centerOfRect(rect);

  // owned=true : l'appelant a deja masque la carte (verrou pose) et nous
  // confie la restauration a l'atterrissage.
  const ownsHide = owned ? targetEl.dataset.flying === '1' : hideRealCard(targetEl);

  const clone = cloneCardAt(card, rect, getCardElement);
  clone.style.left = c.x + 'px';
  clone.style.top = c.y + 'px';
  clone.style.transform = 'translate(-50%,-50%)';

  animateFlight(clone, {
    from: c,
    to,
    duration: 500,
    rotation: { from: 0, to: 0 },
    spin: -8,
    scale: { from: 1, to: 0.96 },
    onLand: () => {
      clone.remove();
      if (ownsHide) restoreRealCard(targetEl);
    },
  });

  // Filet de securite : jamais de clone orphelin ni de carte invisible a vie
  setTimeout(() => {
    clone.remove();
    if (ownsHide) restoreRealCard(targetEl, { flash: false });
  }, 2000);
}


// R34v : restaure toute carte masquée (clone en vol) depuis plus de
// maxAgeMs — un clone orphelin ne doit jamais laisser un monstre invisible.
export function sweepStuckCards(maxAgeMs = 3000) {
  document.querySelectorAll('[data-flying="1"]').forEach((el) => {
    const since = parseInt(el.dataset.flyingSince || '0', 10);
    if (since && Date.now() - since > maxAgeMs) {
      restoreRealCard(el, { flash: false });
    }
  });
}
